import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';

import {
  APP_USAGE_PAGE,
  APP_USAGE_SHIFT_PAGE,
  APP_USAGE_SHIFT_TABLE,
  APP_USAGE_DEVICE_TABLE,
  APP_USAGE_APP_TABLE,
  BATTERY_PAGE,
  BATTERY_TABLE,
  WEBAPP_USAGE_PAGE,
  WEBAPP_USAGE_DEVICE_TABLE,
  WEBAPP_USAGE_APP_TABLE,
  CHART_UI_BAR_TOPN,
  CHART_UI_BAR_TOPN_MANY,
  CHART_UI_DONUT_TOPN,
  DATA_USAGE_PAGE,
  DATA_USAGE_TABLE,
  DEFAULT_DEVICE_FILTER_BAR_CONFIG,
  DEVICE_APP_USAGE,
  DEVICE_WEBAPP_USAGE,
  // DEVICE_SUMMARY,
  DEVICE_BATTERY_USAGE,
  DEVICE_DATA_USAGE,
  DEVICE_LAST_KNOWN,
  DEVICE_SIGNAL_STRENGTH,
  SMART_BATTERY_SPOTLIGHT,
  STARTING_CHARGE_LEVEL_COLORS,
  DEVICE_SUMMARY_CHART_ITEMS,
  WIFI_PAGE,
  WIFI_TABLE,
  CHART_UI_DONUT_LEAST_MOST,
  WF_CHART_UI_BAR_TOPN,
  APP_SUMMARY_CHART_ITEMS,
  APP_SHIFT_SUMMARY_CHART_ITEMS,
  DEVICE_WIFI,
  CHART_UI_BAR_PERCENTAGE,
  WEB_APP_USAGE_SUMMARY_CHART_ITEMS,
} from './app-mapper.constants';

import {
  BarChartConfig,
  ChartTypeEnum,
  DonutChartConfig,
  ElementTypeEnum,
  LineChartConfig,
  MostLeastEnum,
  StackedBarChartConfig,
  SummaryChartConfig,
  TableConfig,
} from 'shared/models/chart.models';
import { HighchartsConstant } from '../../../../../../xs-colors';
import { HourSelectorType, PageCategory } from '../../../shared/features/ng-redux/page/page.models';
import { buildChartOptions, findPageId, patchCard, patchChart, patchPage } from './app-mapper.utils';
import { InsSelectionType, TableRenderEnum } from '@nx-fe/table';
import { CONSTANTS } from '../../../shared/localizations/dictionary';
import { ProfilePermissionEnum } from '@nx-fe/services';

export function mapAppUsageConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [ProfilePermissionEnum.ProfileViewAppUsageDashboard];
  patchPage(app, APP_USAGE_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.group = 'device_filter_app_group';
    app.name = 'Device_Filter_App_Usage';
    page.selectedTab = APP_USAGE_DEVICE_TABLE;
    page.pageCategory = PageCategory.DEVICE;
    page.canManageApps = true;
    page.predefinedCharts = [
      {
        category: {
          name: 'Device_Charts_Category_Name_Caption',
          icon: 'insight-icon-device',
          tooltip: 'App_Usage_Charts_Category_Header_Device_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'appManufacturers', 'Device_Charts_Category_Chart_Caption_Top_Manufacturers'),
          buildChartOptions(page.charts, 'appModel', 'Device_Charts_Category_Chart_Caption_Top_Models'),
          buildChartOptions(page.charts, 'appOSVersion', 'Device_Charts_Category_Chart_Caption_Top_OS_Versions'),
        ],
      },
      {
        category: {
          name: 'App_Usage_Charts_Category_Header_Application',
          icon: '',
          tooltip: 'App_Usage_Charts_Category_Header_Application_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'systemRegular', 'App_Usage_Charts_Category_App_Categories'),
          buildChartOptions(page.charts, 'storageSize', 'App_Usage_Charts_Category_App__Storage_Sizes'),
          buildChartOptions(page.charts, 'visitFrequency', 'App_Usage_Charts_Category_Top_Visited_App'),
          buildChartOptions(page.charts, 'foregroundTime', 'App_Usage_Charts_Category_Foreground_Usage'),
          buildChartOptions(page.charts, 'trendingInstalls', 'App_Usage_Charts_Category_Trending_Installs'),
          buildChartOptions(page.charts, 'leastOpenedAppDays', 'App_Usage_Charts_Category_Apps_Unopened'),
          buildChartOptions(page.charts, 'appVersion', 'App_Usage_Charts_Category_App_Versions'),
          // buildChartOptions(page.charts, 'groupedBarChartTest', 'Grouped Bar Chart test'),
        ],
      },
      // Not listed:
      // appUsageAppTable
      // appUsageDeviceTable
    ];

    patchChart(page.charts, 'appUsageDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.name = 'devices_summary_name';
      chart.layout = '33p';
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.items = APP_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'appManufacturers', (chart: DonutChartConfig) => {
      chart.fullName = 'Manufacturers_fullName';
      chart.reportSummary = 'Manufacturers_reportsummary';
      chart.name = 'app_chart_manufacturers_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_manufacturer_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_manufacturer_tooltip_value", this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.tag = {
        name: 'app_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
    });

    patchChart(page.charts, 'appModel', (chart: DonutChartConfig) => {
      chart.fullName = 'app_chart_model_fullname';
      chart.reportSummary = 'app_chart_model_reportsummary';
      chart.name = 'app_chart_models_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_model_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_model_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_model_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_model_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.tag = {
        name: 'app_chart_models_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
    });

    patchChart(page.charts, 'storageSize', (chart: DonutChartConfig) => {
      chart.name = 'app_chart_storage_size_name';
      chart.fullName = 'app_chart_storage_size_fullName';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_storageSize_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_storageSize_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_storageSize_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_storageSize_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'app_chart_storage_size_name',
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'app_chart_storage_size_name',
        noDataLabel: '{NO_DATA}',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{StorageSize_6}',
          color: HighchartsConstant.appUsageStaticBucket.other,
        },
        {
          label: '{StorageSize_5}',
          color: HighchartsConstant.appUsageStaticBucket.item5,
        },
        {
          label: '{StorageSize_4}',
          color: HighchartsConstant.appUsageStaticBucket.item4,
        },
        {
          label: '{StorageSize_3}',
          color: HighchartsConstant.appUsageStaticBucket.item3,
        },
        {
          label: '{StorageSize_2}',
          color: HighchartsConstant.appUsageStaticBucket.item2,
        },
        {
          label: '{StorageSize_1}',
          color: HighchartsConstant.appUsageStaticBucket.item1,
        },
      ];
    });

    patchChart(page.charts, 'appOSVersion', (chart: BarChartConfig) => {
      chart.fullName = 'app_chart_osVersion_fullname';
      chart.reportSummary = 'app_chart_osVersion_reportsummary';
      chart.name = 'app_chart_OS_version_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_osVersion_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_osVersion_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_osVersion_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_osVersion_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'app_chart_OS_version_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.colorByPoint = true;
      chart.integerOnly = true;
    });

    patchChart(page.charts, 'systemRegular', (chart: DonutChartConfig) => {
      chart.name = 'app_chart_systemRegular_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.reportSummary = 'app_chart_systemRegular_reportsummary';
      chart.fullName = 'app_chart_systemRegular_fullname';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_systemRegular_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_systemRegular_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_systemRegular_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_systemRegular_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'app_chart_systemRegular_name',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });

    patchChart(page.charts, 'visitFrequency', (chart: BarChartConfig) => {
      chart.name = 'app_chart_visit_frequency_name';
      chart.reportSummary = 'app_chart_visit_frequency_reportsummary';
      chart.fullName = 'app_chart_visit_frequency_fullName';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'label_Applications',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'visit_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform(\"app_chart_visitFrequency_tooltip_value\", this.point.y, this.point.totalVisits, (this.point.y/this.point.totalVisits*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribAppDevCount, (this.point.contribAppDevCount/this.point.contribCount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribCount)}`",
        valueArgs: [
          'app_chart_visitFrequency_tooltip_value',
          'this.point.y',
          'this.point.totalVisits',
          'percentage(this.point.y, this.point.totalVisits)',
          'this.point.contribAppDevCount',
          'percentage(this.point.contribAppDevCount, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.tag = {
        name: 'app_chart_visit_frequency_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'foregroundTime', (chart: BarChartConfig) => {
      chart.name = 'app_chart_foreground_name';
      chart.reportSummary = 'app_chart_foreground_reportsummary';
      chart.fullName = 'app_chart_foreground_fullName';
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform(\"app_chart_foregroundTime_tooltip_value\", this.point.yUnit, this.point.totalTimeUnit, (this.point.y/this.point.totalTime*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribAppDevCount, (this.point.contribAppDevCount/this.point.contribCount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribCount)}`",
        valueArgs: [
          'app_chart_foregroundTime_tooltip_value',
          'this.point.yUnit',
          'this.point.totalTimeUnit',
          'percentage(this.point.y, this.point.totalTime)',
          'this.point.contribAppDevCount',
          'percentage(this.point.contribAppDevCount, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.tag = {
        name: 'app_chart_foreground_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'foreground_usage_label',
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'trendingInstalls', (chart: BarChartConfig) => {
      chart.name = 'app_chart_trending_installs_name';
      chart.reportSummary = 'app_chart_trending_installs_reportsummary';
      chart.fullName = 'app_chart_trending_installs_fullName';
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("app_chart_trendingInstalls_tooltip_value", this.point.y, this.point.devcount, (this.point.devcount/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_trendingInstalls_tooltip_value',
          'this.point.y',
          'this.point.devcount',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'app_chart_trending_installs_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'installs_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'leastOpenedAppDays', (chart: BarChartConfig) => {
      chart.name = 'app_chart_unopened_name';
      chart.reportSummary = 'app_chart_unopened_reportsummary';
      chart.fullName = 'app_chart_unopened_fullName';
      chart.tag = {
        name: 'app_chart_unopened_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("app_chart_unopened_tooltip_value", this.point.y, (this.point.devcount/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_unopened_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'appVersion', (chart: StackedBarChartConfig) => {
      chart.name = 'app_chart_app_version_name';
      chart.fullName = 'app_chart_app_version_fullname';
      chart.reportSummary = 'app_chart_app_version_reportsummary';
      chart.integerOnly = true;
      chart.allowPointSelect = true;
      chart.tooltip = {
        label: 'app_chart_app_version_tooltip_label',
        value: 'app_chart_app_version_tooltip_value',
      };
      chart.tag = {
        name: 'app_chart_app_version_name',
        parameters: ['topN'],
      };
      chart.childDimension = {
        dimensionId: 'versionName',
        label: 'app_chart_app_version_childDimension_label',
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.STACKED_BAR;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'appName',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY];
    });

    // patchChart(page.charts, 'groupedBarChartTest', (chart: BarChartConfig) => {
    //   chart.integerOnly = true;
    // });

    patchChart(page.charts, APP_USAGE_DEVICE_TABLE, (chart: TableConfig) => {
      chart.name = 'switcher_devices_title';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
        // {
        //   action: 'attachIncident',
        //   tooltip: 'Attach to Incident',
        //   icon: 'insight-icon-addnew',
        // },
      ];
      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'Column_Name_Device_Name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'Name',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Path',
          label: 'group_path_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'OSVersionAsPaddedString',
        },
        {
          id: 'mostUsedApp',
          label: 'mostUsedApp_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          label: 'visit_label',
          renderer: TableRenderEnum.DEFAULT,
          iconClass: '',
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },
        {
          id: 'storageFreeLastKnown',
          label: 'availablestorage_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          shouldTranslate: true,
        },
        {
          id: 'appsInstalled',
          label: 'appsInstalled_label',
          renderer: TableRenderEnum.NUMBER,
          iconClass: '',
        },
        {
          id: 'appsUnopened',
          label: 'leastOpenedAppDays_name_Apps_Unopened',
          renderer: TableRenderEnum.NUMBER,
          iconClass: '',
        },
        {
          id: 'appVersion',
          label: 'appVersion_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });

    patchChart(page.charts, APP_USAGE_APP_TABLE, (chart: TableConfig) => {
      chart.name = 'label_Applications';
      chart.tableAttributes = [
        {
          id: 'appName',
          label: 'xs_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
          },
        },
        {
          id: 'install',
          label: 'installations_label',
          renderer: TableRenderEnum.NUMBER,
        },
        {
          id: 'updates',
          label: 'updates_label',
          renderer: TableRenderEnum.NUMBER,
        },
        {
          id: 'visits',
          label: 'visit_label',
          renderer: TableRenderEnum.NUMBER,
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },
        {
          id: 'unopenedCount',
          label: 'unopened_on_label',
          renderer: TableRenderEnum.NUMBER,
        },
        {
          id: 'type',
          label: 'app_category_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'storageDisplayMB',
          label: 'app_storage_size_label',
          renderer: TableRenderEnum.STRING,
          primaryColumnSortId: 'totalStorage',
        },
      ];
    });
  });

  const pageIds = [
    findPageId(app.pages, APP_USAGE_PAGE),
    // findPageId(devApp.pages, DEVICE_SUMMARY),
  ];

  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_APP_USAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}

export function mapAppUsageShiftConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.claims = [ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard];
  app.icon = 'insight-icon-application-shift';
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  patchPage(app, APP_USAGE_SHIFT_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.group = 'device_filter_app_group';
    app.name = 'aps_name_abbr';
    page.selectedTab = APP_USAGE_SHIFT_TABLE;
    page.pageCategory = PageCategory.DEVICE;
    page.shiftEnabled = true;
    page.canManageApps = true;
    page.dateFilterTypes = [
      'Custom Date',
      'Today',
      'Yesterday',
      'Last Week',
      'Last Month',
      'Last 7 Days',
      'Last 30 Days',
    ];

    page.predefinedCharts = [
      {
        category: {
          name: 'aps_category_device',
          icon: 'insight-icon-device',
          tooltip: 'aps_category_device_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'apsManufacturers', 'aps_chart_manufacturers_name_caption'),
          buildChartOptions(page.charts, 'apsModel', 'aps_chart_model_name_caption'),
          // buildChartOptions(page.charts, 'apsVersion', 'Top App Versions'),
        ],
      },
      {
        category: {
          name: 'aps_category_app',
          icon: '',
          tooltip: 'aps_category_app_tip',
        },
        charts: [
          buildChartOptions(page.charts, 'apsShift', 'aps_chart_shift_name'),
          buildChartOptions(page.charts, 'apsCategories', 'aps_chart_categories_name'),
          buildChartOptions(page.charts, 'apsApplications', 'label_Applications'),
          buildChartOptions(page.charts, 'apsVisits', 'aps_chart_visits_name'),
          buildChartOptions(page.charts, 'apsUsage', 'aps_chart_usage_name'),
          // buildChartOptions(page.charts, 'groupedBarChartTest', 'Grouped Bar Chart test'),
        ],
      },
      // Not listed:
      // appUsageAppTable
      // appUsageDeviceTable
    ];

    patchChart(page.charts, 'apsDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.name = 'devices_summary_name';
      chart.fullName = undefined;
      chart.layout = '33p';
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.items = APP_SHIFT_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'apsUsage', (chart: BarChartConfig) => {
      chart.name = 'aps_chart_usage_name_specific';
      chart.reportSummary = 'aps_chart_usage_reportsummary';
      chart.fullName = 'aps_chart_usage_fullname';
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'foreground_label',
      };
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.type = ChartTypeEnum.COLUMN;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform(\"aps_chart_usage_tooltip_value\", this.point.yUnit, this.point.totalTimeUnit, (this.point.y/this.point.totalTime*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribAppDevCount, (this.point.contribAppDevCount/this.point.contribCount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribCount)}`",
        valueArgs: [
          'aps_chart_usage_tooltip_value',
          'this.point.yUnit',
          'this.point.totalTimeUnit',
          'percentage(this.point.y, this.point.totalTime)',
          'this.point.contribAppDevCount',
          'percentage(this.point.contribAppDevCount, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_usage_name_specific',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.scrollableX = true;
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'apsManufacturers', (chart: DonutChartConfig) => {
      chart.name = 'aps_chart_manufacturers_name';
      chart.fullName = 'aps_chart_manufacturers_fullname';
      chart.reportSummary = 'aps_chart_manufacturers_reportsummary';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("aps_chart_manufacturers_tooltip_label", this.point.name)}`',
        labelArgs: ['aps_chart_manufacturers_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("aps_chart_manufacturers_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'aps_chart_manufacturers_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'apsModel', (chart: DonutChartConfig) => {
      chart.name = 'aps_chart_model_name';
      chart.fullName = 'aps_chart_model_fullname';
      chart.reportSummary = 'aps_chart_model_reportsummary';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("aps_chart_model_tooltip_label", this.point.name)}`',
        labelArgs: ['aps_chart_model_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("aps_chart_model_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'aps_chart_model_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_model_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'apsVisits', (chart: BarChartConfig) => {
      chart.fullName = 'aps_chart_visit_fullName';
      chart.reportSummary = 'aps_chart_visit_reportsummary';
      chart.name = 'aps_chart_visit_name';
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.type = ChartTypeEnum.COLUMN;
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform(\"aps_chart_visits_tooltip_value\", this.point.y, this.point.totalVisits, ((this.point.y/this.point.totalVisits)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribAppDevCount, ((this.point.contribAppDevCount/this.point.contribCount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.contribCount)}`",
        valueArgs: [
          'aps_chart_visits_tooltip_value',
          'this.point.y',
          'this.point.totalVisits',
          'percentage(this.point.y, this.point.totalVisits)',
          'this.point.contribAppDevCount',
          'percentage(this.point.contribAppDevCount, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_visit_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'visit_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.scrollableX = true;
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'apsApplications', (chart: BarChartConfig) => {
      chart.name = 'label_Applications';
      chart.fullName = 'aps_chart_applications_fullName';
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.inverted = true;
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.layout = '33p';
      chart.dimension = {
        dimensionId: 'label',
        label: 'application_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("xs_app_name_label", this.point.label, this.point.packageId)}`',
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          '`${xsI18nPipe.transform("aps_chart_applications_tooltip_value", this.point.y, (this.point.y/this.point.contribCount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.contribCount)}`',
        valueArgs: [
          'aps_chart_applications_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.tag = {
        name: 'as_table_app_name',
      };
    });

    patchChart(page.charts, 'apsShift', (chart: BarChartConfig) => {
      chart.reportSummary = 'aps_chart_shift_reportsummary';
      chart.fullName = 'aps_chart_shift_fullName';
      chart.name = 'aps_chart_shift_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.COLUMN;
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'interval',
        label: 'time_of_day_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'total_foreground_label',
      };
      chart.colors = ['#2E86C1'];
      chart.interval = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("aps_chart_shift_tooltip_label", this.point.interval)}`',
        labelArgs: ['aps_chart_shift_tooltip_label', 'this.point.interval'],
        value:
          '`${xsI18nPipe.transform("aps_chart_shift_tooltip_value", this.point.contribAppDevCount, (this.point.contribAppDevCount/this.point.contribCount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.contribCount)}`',
        valueArgs: [
          'aps_chart_shift_tooltip_value',
          'this.point.contribAppDevCount',
          'percentage(this.point.contribAppDevCount, this.point.contribCount)',
          'this.point.contribCount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_shift_name',
      };
    });

    patchChart(page.charts, 'apsCategories', (chart: DonutChartConfig) => {
      chart.fullName = 'aps_chart_categories_fullname';
      chart.reportSummary = 'aps_chart_categories_reportsummary';
      chart.name = 'aps_chart_categories_name';
      chart.elementType = 'chart';
      chart.type = ChartTypeEnum.DONUT;
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("aps_chart_categories_tooltip_label", this.point.name)}`',
        labelArgs: ['aps_chart_categories_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("aps_chart_categories_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'aps_chart_categories_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'aps_chart_categories_name',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });

    patchChart(page.charts, APP_USAGE_SHIFT_TABLE, (chart: TableConfig) => {
      chart.name = 'Ticket_List_Devices_Header';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
        // {
        //   action: 'attachIncident',
        //   tooltip: 'Attach to Incident',
        //   icon: 'insight-icon-addnew',
        // },
      ];
      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'aps_table_device_mcDeviceName_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'Name',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Path',
          label: 'group_path_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Manufacturer',
          label: 'label_Manufacturer',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'OSVersionAsPaddedString',
        },
        {
          id: 'mostUsedApp',
          label: 'mostUsedApp_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          label: 'visit_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'consumedTime',
          label: 'total_foreground_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },
      ];
    });
  });

  const pageIds = [
    findPageId(app.pages, APP_USAGE_SHIFT_PAGE),
    // findPageId(devApp.pages, DEVICE_SUMMARY),
  ];

  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_APP_USAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}

export function mapBatteryUsageConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [ProfilePermissionEnum.ProfileViewBatteryStatusDashboard];
  patchPage(app, BATTERY_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.name = 'Device_Filter_Battery_Status';
    page.pageCategory = PageCategory.DEVICE;
    page.predefinedCharts = [
      {
        category: {
          name: 'Device_Charts_Category_Name_Caption',
          icon: 'insight-icon-device',
          tooltip: 'Device_Charts_Category_Name_Tooltip_Caption',
        },
        charts: [
          buildChartOptions(
            page.charts,
            'batteryManufacturers_sql',
            'Device_Charts_Category_Chart_Caption_Top_Manufacturers'
          ),
          buildChartOptions(page.charts, 'batteryDeviceModels_sql', 'Device_Charts_Category_Chart_Caption_Top_Models'),
          buildChartOptions(
            page.charts,
            'batteryOSVersion_sql',
            'Device_Charts_Category_Chart_Caption_Top_OS_Versions'
          ),
        ],
      },
      {
        category: {
          // 4
          name: 'Device_Charts_Category_Battery_Caption',
          icon: 'insight-icon-battery-usage',
          tooltip: 'Device_Charts_Category_Battery_Tooltip_Caption',
        },
        charts: [
          buildChartOptions(
            page.charts,
            'batterySize_sql',
            'Device_Charts_Category_Chart_Caption_Top_Battery_Capacities'
          ),
          buildChartOptions(
            page.charts,
            'batteryHealth_sql',
            'Device_Charts_Category_Chart_Caption_Top_Remaining_Battery_Capacity'
          ),
          buildChartOptions(
            page.charts,
            'batteryTemperature_sql',
            'Device_Charts_Category_Chart_Caption_Top_Battery_Temperature'
          ),
          buildChartOptions(page.charts, 'appDrain_sql', 'Device_Charts_Category_Chart_Caption_Top_App_Drain'),
        ],
      },
      {
        category: {
          // 5
          name: 'Device_Charts_Category_Charging_Caption',
          icon: 'insight-icon-battery-charging',
          tooltip: 'Device_Charts_Category_Charging_Tooltip_Caption',
        },
        charts: [
          buildChartOptions(page.charts, 'chargeCycles_sql', 'Device_Charts_Category_Chart_Caption_Charge_Cycles'),
          buildChartOptions(
            page.charts,
            'cumulativeChargeCycles_sql',
            'Device_Charts_Category_Chart_Caption_Cumulative_Charge_Cycles'
          ),
          buildChartOptions(page.charts, 'chargePattern_sql', 'Device_Charts_Category_Chart_Caption_Charge_Pattern'),
          buildChartOptions(page.charts, 'chargeType_sql', 'Device_Charts_Category_Chart_Caption_Charge_Type'),
          buildChartOptions(
            page.charts,
            'startingChargeLevel_sql',
            'Device_Charts_Category_Chart_Caption_Charge_Level'
          ),
          buildChartOptions(page.charts, 'dischargeRate_sql', 'Device_Charts_Category_Chart_Caption_Discharge_Rate'),
        ],
      },
      // Not listed:
      // batstatstable
    ];

    patchChart(page.charts, 'batstatDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.name = 'devices_summary_name';
      chart.layout = '33p';
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.items = DEVICE_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'batteryManufacturers_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'Manufacturers_fullName';
      chart.reportSummary = 'Manufacturers_reportsummary';
      chart.name = 'battery_chart_manufacturers_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_manufacturer_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_manufacturer_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'batteryDeviceModels_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'Model_fullName';
      chart.reportSummary = 'Model_reportsummary';
      chart.name = 'battery_chart_models_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_models_name',
        parameters: ['topN', 'mostLeast'],
      };

      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_models_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_models_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_models_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_models_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'Manufacturer',
        noDataLabel: 'noData',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };

      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'batterySize_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'batterySize_sql_fullName';
      chart.reportSummary = 'batterySize_sql_reportsummary';
      chart.name = 'battery_chart_size_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_size_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'batterySize_sql_fullName',
        noDataLabel: '{NO_DATA}',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_battery_size_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_battery_size_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_battery_size_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_battery_size_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'appDrain_sql', (chart: BarChartConfig) => {
      chart.fullName = 'appDrain_sql_fullName';
      chart.reportSummary = 'appDrain_sql_reportsummary';
      chart.name = 'battery_chart_app_drain_name';
      chart.layout = '50p';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.dimension = {
        dimensionId: 'label',
        label: 'battery_chart_app_drain_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'percentTotalAppDrain',
        label: 'battery_chart_app_drain_metric_label',
        metricFormat: 'percentage',
      };
      chart.tag = {
        name: 'battery_chart_app_drain_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };

      chart.tooltip = {
        label: "`${xsI18nPipe.transform('xs_app_name_label', this.point.label, this.point.packageId)}`",
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform('battery_chart_app_drain_tooltip_value', this.point.y, this.point.devcount,  (this.point.devcount/this.point.filteredcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.filteredcount)}`",
        valueArgs: [
          'battery_chart_app_drain_tooltip_value',
          'this.point.y',
          'this.point.devcount',
          'percentage(this.point.devcount, this.point.filteredcount)',
          'this.point.filteredcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'chargeCycles_sql', (chart: BarChartConfig) => {
      chart.integerOnly = true;
      chart.fullName = 'chargeCycles_sql_fullName';
      chart.name = 'battery_chart_charge_cycles_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';

      chart.dimension = {
        dimensionId: 'name',
        label: 'battery_chart_charge_cycles_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tag = {
        name: 'category_Charging_Average_Daily_Charge_Cycles',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_charge_cycles_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_charge_cycles_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_charge_cycles_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_charge_cycles_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
    });
    patchChart(page.charts, 'cumulativeChargeCycles_sql', (chart: BarChartConfig) => {
      chart.fullName = 'cumulativeChargeCycles_sql_fullName';
      chart.name = 'battery_chart_cumulative_charge_cycles_name';
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_cumulative_charge_cycles_name',
      };
      chart.noDataText = '{insufficient_data}';
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_total_charge_cycles_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_total_charge_cycles_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_total_charge_cycles_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_total_charge_cycles_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.integerOnly = true;
      chart.uiSettings = [
        {
          id: 'batteryCycles',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'app_mapper_smart_Select_the_cycles_ranges',
          options: [],
        },
      ];
    });
    patchChart(page.charts, 'chargeType_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'chargeType_sql_fullName';
      chart.reportSummary = 'chargeType_sql_reportsummary';
      chart.name = 'battery_chart_charge_type_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.noDataText = '{ChargeType_4}';
      chart.tag = {
        name: 'battery_chart_charge_type_name',
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'battery_chart_charge_pattern_name',
        noDataLabel: '{ChargeType_4}',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_charging_type_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_charging_type_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_charging_type_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_charging_type_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{ChargeType_1}',
          color: HighchartsConstant.chargeTypeBucket.wireless,
        },
        {
          label: '{ChargeType_2}',
          color: HighchartsConstant.chargeTypeBucket.ac,
        },
        {
          label: '{ChargeType_3}',
          color: HighchartsConstant.chargeTypeBucket.usb,
        },
        {
          label: '{ChargeType_5}',
          color: HighchartsConstant.chargeTypeBucket.dock,
        },
      ];
    });
    patchChart(page.charts, 'dischargeRate_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'app-mapper-dischargeRate_sql-fullName';
      chart.name = 'battery_chart_discharge_rate_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'Device_Charts_Category_Chart_Caption_Discharge_Rate',
      };
      chart.uiSettings = [
        {
          id: 'dischargeRate',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'discharge_rate_ranges_label',
          options: [],
        },
      ];
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_discharge_rate_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_discharge_rate_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_discharge_rate_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_discharge_rate_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{DischargeRate_4}',
          color: HighchartsConstant.dischargeBucket.low,
        },
        {
          label: '{DischargeRate_3}',
          color: HighchartsConstant.dischargeBucket.average,
        },
        {
          label: '{DischargeRate_2}',
          color: HighchartsConstant.dischargeBucket.high,
        },
        {
          label: '{DischargeRate_1}',
          color: HighchartsConstant.dischargeBucket.veryHigh,
        },
      ];
      chart.dimension = {
        dimensionId: 'name',
        label: 'battery_chart_discharge_rate_name',
        noDataLabel: '{DischargeRate_100}',
      };
    });
    patchChart(page.charts, 'batteryOSVersion_sql', (chart: BarChartConfig) => {
      chart.fullName = 'OSVersion_fullName';
      chart.reportSummary = 'OSVersion_reportsummary';
      chart.name = 'battery_chart_os_version_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';

      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tag = {
        name: 'battery_chart_os_version_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_os_version_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_os_version_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.colorByPoint = true;
      chart.integerOnly = true;
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, BATTERY_TABLE, (chart: TableConfig) => {
      chart.name = 'Ticket_List_Devices_Header';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
        // {
        //   action: 'attachIncident',
        //   tooltip: 'Attach to Incident',
        //   icon: 'insight-icon-addnew',
        // },
      ];
      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'Column_Name_Device_Name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'Name',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'GroupPath',
          label: 'group_path_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'BatteryHealth',
          label: 'battery_chart_health_name',
          renderer: TableRenderEnum.STRING,
          shouldTranslate: true,
        },
        {
          id: 'DischargeRate',
          label: 'DischargeRate_label',
          renderer: TableRenderEnum.PROGRESS_BAR,
        },
        {
          id: 'ModelName',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'ChargePatternBucketLabel',
          label: 'battery_chart_charge_pattern_name',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          shouldTranslate: true,
        },
        {
          id: 'ChargeTypeBucketLabel',
          label: 'battery_chart_charge_type_name',
          renderer: TableRenderEnum.ICON,
          iconClass: 'insight-icon-chargetype',
          shouldTranslate: true,
        },
        {
          id: 'BatteryTemperatureBucketLabel',
          label: 'temperature_label',
          renderer: TableRenderEnum.STRING,
          shouldTranslate: true,
        },
        {
          id: 'ChargeCycles',
          label: 'AvgChargeCycles_label',
          renderer: TableRenderEnum.NUMBER,
          rendererParams: { decimals: 2 },
        },
        {
          id: 'CumulativeChargeCycles',
          label: 'CumulativeChargeCycles_label',
          renderer: TableRenderEnum.STRING,
          primaryColumnSortId: 'BatteryLevelDropBucketLabel',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'batteryCapacityRounded',
          label: 'batteryCapacityRounded_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'ManufacturerName',
          label: 'label_Manufacturer',
          renderer: TableRenderEnum.STRING,
        },
      ];
    });
    patchChart(page.charts, 'startingChargeLevel_sql', (chart: BarChartConfig) => {
      chart.fullName = 'startingChargeLevel_sql_fullName';
      chart.reportSummary = 'startingChargeLevel_sql_reportsummary';
      chart.name = 'battery_chart_charge_level_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.noDataText = '{NO_DATA}';
      chart.dimension = {
        dimensionId: 'label',
        label: 'battery_chart_chargelevel_dimension_label',
        noDataLabel: '{NO_DATA}',
      };
      chart.metric = {
        metricId: 'deviceCount',
        label: 'device_count_label',
      };
      chart.tag = {
        name: 'battery_chart_charge_level_name',
        parameters: ['hour'],
      };
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('battery_chart_charge_level_tooltip_label', xsI18nPipe.transform(this.point.hr), xsI18nPipe.transform(this.point.label))}`",
        labelArgs: [
          'battery_chart_charge_level_tooltip_label',
          'xsI18nPipe.transform(this.point.hr)',
          'xsI18nPipe.transform(this.point.label)',
        ],
        value:
          "`${xsI18nPipe.transform('battery_chart_charge_level_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_charge_level_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        hour: 8,
      };
      chart.integerOnly = true;
      chart.fixedColors = STARTING_CHARGE_LEVEL_COLORS;
      chart.uiSettings = [
        {
          id: 'hour',
          controlType: 'SELECTBOX',
          type: 'number',
          displayType: 'dropdown',
          options: [
            {
              value: 0,
              label: '0_hour',
            },
            {
              value: 1,
              label: '1_hour',
            },
            {
              value: 2,
              label: '2_hour',
            },
            {
              value: 3,
              label: '3_hour',
            },
            {
              value: 4,
              label: '4_hour',
            },
            {
              value: 5,
              label: '5_hour',
            },
            {
              value: 6,
              label: '6_hour',
            },
            {
              value: 7,
              label: '7_hour',
            },
            {
              value: 8,
              label: '8_hour',
            },
            {
              value: 9,
              label: '9_hour',
            },
            {
              value: 10,
              label: '10_hour',
            },
            {
              value: 11,
              label: '11_hour',
            },
            {
              value: 12,
              label: '12_hour',
            },
            {
              value: 13,
              label: '13_hour',
            },
            {
              value: 14,
              label: '14_hour',
            },
            {
              value: 15,
              label: '15_hour',
            },
            {
              value: 16,
              label: '16_hour',
            },
            {
              value: 17,
              label: '17_hour',
            },
            {
              value: 18,
              label: '18_hour',
            },
            {
              value: 19,
              label: '19_hour',
            },
            {
              value: 20,
              label: '20_hour',
            },
            {
              value: 21,
              label: '21_hour',
            },
            {
              value: 22,
              label: '22_hour',
            },
            {
              value: 23,
              label: '23_hour',
            },
          ],
        },
      ];
    });
    patchChart(page.charts, 'batteryHealth_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'batteryHealth_sql_fullName';
      chart.name = 'battery_chart_health_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_health_name',
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'battery_chart_health_name',
        noDataLabel: '{insufficient_data}',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_health_tooltip_label', xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['battery_chart_health_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('battery_chart_health_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_health_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [
        {
          id: 'batteryCapacity',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'uiSettings_name_capacity_ranges',
          options: [],
        },
      ];
    });
    patchChart(page.charts, 'batteryTemperature_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'batteryTemperature_sql_fullName';
      chart.name = 'battery_chart_temperature_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'battery_chart_temperature_name',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_temperature_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_temperature_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_temperature_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_temperature_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [
        {
          id: 'batteryTemperature',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'sdd_battery_chart_temperature_settings_threshold_message',
          options: [],
        },
      ];
      chart.fixedColors = [
        {
          label: '{BatteryTemperature_1}',
          color: HighchartsConstant.batteryTemperatureBucket.normal,
        },
        {
          label: '{BatteryTemperature_2}',
          color: HighchartsConstant.batteryTemperatureBucket.hot,
        },
        {
          label: '{BatteryTemperature_3}',
          color: HighchartsConstant.batteryTemperatureBucket.cold,
        },
        {
          label: '{BatteryTemperature_4}',
          color: HighchartsConstant.batteryTemperatureBucket.both,
        },
      ];
    });
    patchChart(page.charts, 'chargePattern_sql', (chart: DonutChartConfig) => {
      chart.fullName = 'chargePattern_sql_fullName';
      chart.name = 'battery_chart_charge_pattern_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.noDataText = '{NO_DATA}';
      chart.tag = {
        name: 'battery_chart_charge_pattern_name',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('battery_chart_charge_pattern_tooltip_label', this.point.name)}`",
        labelArgs: ['battery_chart_charge_pattern_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('battery_chart_charge_pattern_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'battery_chart_charge_pattern_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'battery_chart_charge_pattern_name',
        noDataLabel: '{ChargePattern_100}',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{ChargePattern_1}',
          color: HighchartsConstant.chargingPatternBucket.overCharged,
        },
        {
          label: '{ChargePattern_2}',
          color: HighchartsConstant.chargingPatternBucket.optimal,
        },
        {
          label: '{ChargePattern_3}',
          color: HighchartsConstant.chargingPatternBucket.spot,
        },
        {
          label: '{ChargePattern_4}',
          color: HighchartsConstant.chargingPatternBucket.random,
        },
      ];
    });
  });

  const pageIds = [
    findPageId(app.pages, BATTERY_PAGE),
    // findPageId(devApp.pages, DEVICE_SUMMARY),
  ];
  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_BATTERY_USAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}

export function mapDataUsageConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [ProfilePermissionEnum.ProfileViewDataUsageDashboard];
  patchPage(app, DATA_USAGE_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.group = 'device_filter_network_group';
    app.name = 'data_usage_label';
    page.pageCategory = PageCategory.DEVICE;
    page.predefinedCharts = [
      {
        category: {
          name: 'Device_Charts_Category_Name_Caption',
          icon: 'insight-icon-device',
          tooltip: 'App_Usage_Charts_Category_Header_Device_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dataManufacturers', 'Device_Charts_Category_Chart_Caption_Top_Manufacturers'),
          buildChartOptions(page.charts, 'dataModel', 'Device_Charts_Category_Chart_Caption_Top_Models'),
          buildChartOptions(page.charts, 'dataOSVersion', 'Device_Charts_Category_Chart_Caption_Top_OS_Versions'),
        ],
      },
      {
        category: {
          // 11
          name: 'Data_Usage_Charts_Category_Header_Network',
          icon: '',
          tooltip: 'Data_Usage_Charts_Category_Header_Network_Tooltip',
        },
        charts: [buildChartOptions(page.charts, 'dataConnectionType', 'data_chart_connectiontype_name')],
      },
      {
        category: {
          // 10
          name: 'Data_Usage_Charts_Category_Header_Usage',
          icon: '',
          tooltip: 'Data_Usage_Charts_Category_Header_Usage_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dataUsageByUploadDownload', 'Data_Usage_Charts_Category_Upload'),
          buildChartOptions(page.charts, 'dataUsageByAppName', 'Data_Usage_Charts_Category_Top_Apps_Data_Usage'),
          buildChartOptions(page.charts, 'dataUsageByBrowser', 'Data_Usage_Charts_Category_Top_Browsers_Data_Usage'),
          buildChartOptions(page.charts, 'dataTotalTS', 'Data_Usage_Charts_Category_Total_Data_Usage'),
          buildChartOptions(page.charts, 'dataRoamDevice', 'Data_Usage_Charts_Category_Roaming_Usage'),
          buildChartOptions(page.charts, 'dataRoamUsageTS', 'data_usage_roaming_usage_ts_name'),
          buildChartOptions(page.charts, 'dataTetherDevice', 'Data_Usage_Charts_Category_Tether_Device'),
          buildChartOptions(page.charts, 'dataTetherTS', 'data_usage_tether_data_ts_name'),
        ],
      },
      {
        category: {
          name: 'Data_Usage_Charts_Category_Header_time',
          icon: '',
          tooltip: 'Data_Usage_Charts_Category_Header_time_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'dataAvgConnectionTimeTS', 'data_usage_avg_conn_time_ts_name'),
          buildChartOptions(page.charts, 'dataConnectionByTime', 'Data_Usage_Charts_Category_Connection_Usage_Time'),
          buildChartOptions(page.charts, 'dataRoamTimeTS', 'data_usage_roaming_time_ts_name'),
          buildChartOptions(page.charts, 'dataVpnTS', 'data_usage_vpn_time_ts_name'),
        ],
      },
      // Not Listed:
      // datausagetable
    ];

    patchChart(page.charts, 'dataUsageDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.name = 'devices_summary_name';
      chart.layout = '33p';
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.items = DEVICE_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'dataManufacturers', (chart: DonutChartConfig) => {
      chart.fullName = 'Manufacturers_fullName';
      chart.reportSummary = 'Manufacturers_reportsummary';
      chart.name = 'data_chart_manufacturers_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'data_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_manufacturer_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_manufacturer_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataModel', (chart: DonutChartConfig) => {
      chart.fullName = 'Model_fullName';
      chart.reportSummary = 'Model_reportsummary';
      chart.name = 'data_chart_models_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'data_chart_models_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_model_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_model_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_models_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_models_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataOSVersion', (chart: BarChartConfig) => {
      chart.fullName = 'OSVersion_fullName';
      chart.reportSummary = 'OSVersion_reportsummary';
      chart.name = 'data_chart_os_versions_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.tag = {
        name: 'data_chart_os_versions_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.colorByPoint = true;
      chart.integerOnly = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_os_version_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_os_version_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataConnectionType', (chart: DonutChartConfig) => {
      chart.fullName = 'data_chart_connectiontype_fullname';
      chart.reportSummary = 'data_chart_connectiontype_reportsummary';
      chart.name = 'data_chart_connectiontype_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'data_chart_connectiontype_name',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_connection_type_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_connection_type_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_connection_type_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,   this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''),this.point.contributingDevices, ((this.point.contributingDevices/this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_chart_connection_type_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributingDevices',
          'percentage(this.point.contributingDevices, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: '',
        noDataLabel: 'no_data_label',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{ConnectionType_2}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{ConnectionType_3}',
          color: HighchartsConstant.dataUsageStaticBucket.item4,
        },
        {
          label: '{ConnectionType_1}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchChart(page.charts, 'dataUsageByUploadDownload', (chart: DonutChartConfig) => {
      chart.reportSummary = 'data_chart_data_usage_upload_reportsummary';
      chart.fullName = 'data_chart_data_usage_upload_fullname';
      chart.name = 'data_chart_data_usage_upload_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'data_chart_data_usage_upload_name',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_usage_upload_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_usage_upload_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_usage_upload_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,   this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''), this.point.contributedCount ,((this.point.contributedCount /this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_chart_usage_upload_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributedCount',
          'percentage(this.point.contributedCount, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.fixedColors = [
        {
          label: '{DATA_UPLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item1,
        },
        {
          label: '{DATA_DOWNLOAD}',
          color: HighchartsConstant.dataUsageStaticBucket.item2,
        },
      ];
    });

    patchChart(page.charts, 'dataUsageByAppName', (chart: BarChartConfig) => {
      chart.reportSummary = 'data_chart_data_usage_app_reportsummary';
      chart.fullName = 'data_chart_data_usage_app_fullname';
      chart.name = 'data_chart_data_usage_app_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.dimension = {
        dimensionId: 'label',
        label: 'data_chart_usage_app_name_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'appUsage',
        label: 'data_usage_metric_label',
        metricFormat: 'data',
      };
      chart.tag = {
        name: 'data_chart_data_usage_app_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.integerOnly = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.scrollableX = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('xs_app_name_label', this.point.label, this.point.packageId)}`",
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform('data_chart_usage_app_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,  this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''), this.point.contributedCount, ((this.point.contributedCount/this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_usage_app_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributedCount',
          'percentage(this.point.contributedCount, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataUsageByBrowser', (chart: BarChartConfig) => {
      chart.fullName = 'data_chart_data_usage_browser_fullname';
      chart.reportSummary = 'data_chart_data_usage_browser_reportsummary';
      chart.name = 'data_chart_data_usage_browser_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.dimension = {
        dimensionId: 'label',
        label: 'data_chart_usage_browser_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'allUsage',
        label: 'data_usage_metric_label',
        metricFormat: 'data',
      };
      chart.tag = {
        name: 'data_chart_data_usage_browser_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('xs_app_name_label', this.point.label, this.point.packageId)}`",
        labelArgs: ['xs_app_name_label', 'this.point.label', 'this.point.packageId'],
        value:
          "`${xsI18nPipe.transform('data_chart_usage_browser_tooltip_value', this.point.yUnit, this.point.totalUsageUnit,   this.point.usageRatio.toFixed(2).replace(/\\.?0+$/,''),this.point.contributedCount, ((this.point.contributedCount/this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_chart_usage_browser_tooltip_value',
          'this.point.yUnit',
          'this.point.totalUsageUnit',
          'this.point.usageRatio',
          'this.point.contributedCount',
          'percentage(this.point.contributedCount, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataTotalTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_chart_data_usage_time_fullname';
      chart.name = 'data_chart_data_usage_time_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '100p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'allUsage',
        label: 'data_use_metric_label',
      };
      chart.tag = {
        name: 'data_chart_data_usage_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_chart_data_usage_time_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'dataAvgConnectionTimeTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_usage_avg_conn_time_ts_fullname';
      chart.name = 'data_usage_avg_conn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'data_usage_avg_conn_time_ts_metric_label',
      };
      chart.metrics = [
        {
          id: 'timeCell',
          timeFormatValueId: 'hourMinute1',
          countId: 'devCell',
        },
        {
          id: 'timeWifi',
          timeFormatValueId: 'hourMinute2',
          countId: 'devWiFi',
        },
      ];
      chart.tag = {
        name: 'data_usage_avg_conn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_usage_avg_conn_time_ts_tooltip_value',
      };
      chart.legendDisabled = false;
      chart.legends = ['data_usage_avg_conn_time_ts_legend_cellular', 'data_usage_avg_conn_time_ts_legend_wifi'];
      chart.formatDate = true;
      chart.formatTime = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });

    patchChart(page.charts, 'dataConnectionByTime', (chart: BarChartConfig) => {
      chart.fullName = 'data_usage_data_conn_by_time_fullname';
      chart.name = 'data_usage_data_conn_by_time_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.dimension = {
        dimensionId: 'name',
        label: 'data_usage_data_conn_by_time_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_data_conn_by_time_metric_label',
        metricFormat: 'data',
      };
      chart.tag = {
        name: 'data_usage_data_conn_by_time_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.integerOnly = true;
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('data_usage_data_conn_by_time_tooltip_label', xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['data_usage_data_conn_by_time_tooltip_label', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('data_usage_data_conn_by_time_tooltip_value', this.point.usageTimePct, this.point.y, ((this.point.y/this.point.devcount)*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount )}`",
        valueArgs: [
          'data_usage_data_conn_by_time_tooltip_value',
          'this.point.usageTimePct',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        topN: 50,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_BAR_PERCENTAGE, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataRoamDevice', (chart: BarChartConfig) => {
      chart.fullName = 'data_usage_roaming_usage_fullname';
      chart.name = 'data_usage_roaming_usage_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_usage_roaming_usage_tooltip_label', this.point.name)}`",
        labelArgs: ['data_usage_roaming_usage_tooltip_label', 'this.point.name'],
        value: "`${xsI18nPipe.transform('data_usage_roaming_usage_tooltip_value', this.point.yUnit)}`",
        valueArgs: ['data_usage_roaming_usage_tooltip_value', 'this.point.yUnit'],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'data_usage_roaming_usage_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_roaming_usage_metric_label',
        metricFormat: 'data',
      };
      chart.tag = {
        name: 'data_usage_roaming_usage_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.inverted = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataRoamUsageTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_usage_roaming_usage_ts_fullname';
      chart.name = 'data_usage_roaming_usage_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_roaming_usage_ts_metric_label',
      };
      chart.tag = {
        name: 'data_usage_roaming_usage_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_usage_roaming_usage_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'dataRoamTimeTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_usage_roaming_time_ts_fullname';
      chart.name = 'data_usage_roaming_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_roaming_time_ts_metric_label',
      };
      chart.tag = {
        name: 'data_usage_roaming_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_usage_roaming_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.formatTime = true;
      chart.integerOnly = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'dataTetherDevice', (chart: BarChartConfig) => {
      chart.fullName = 'data_usage_tether_device_fullname';
      chart.name = 'data_usage_tether_device_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_usage_tether_device_tooltip_label', this.point.name)}`",
        labelArgs: ['data_usage_tether_device_tooltip_label', 'this.point.name'],
        value: "`${xsI18nPipe.transform('data_usage_tether_device_tooltip_value', this.point.yUnit)}`",
        valueArgs: ['data_usage_tether_device_tooltip_value', 'this.point.yUnit'],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'data_usage_tether_device_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_tether_device_metric_label',
      };
      chart.tag = {
        name: 'data_usage_tether_device_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.inverted = true;
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'dataTetherTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_usage_tether_data_ts_fullname';
      chart.name = 'data_usage_tether_data_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_tether_data_ts_metric_label',
      };
      chart.tag = {
        name: 'data_usage_tether_data_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_usage_tether_data_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, 'dataVpnTS', (chart: LineChartConfig) => {
      chart.fullName = 'data_usage_vpn_time_ts_fullname';
      chart.name = 'data_usage_vpn_time_ts_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '66p';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'data_usage_vpn_time_ts_metric_label',
      };
      chart.tag = {
        name: 'data_usage_vpn_time_ts_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'data_usage_vpn_time_ts_tooltip_value',
      };
      chart.formatDate = true;
      chart.formatTime = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });

    patchChart(page.charts, DATA_USAGE_TABLE, (chart: TableConfig) => {
      chart.name = 'Ticket_List_Devices_Header';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
        // {
        //   action: 'attachIncident',
        //   tooltip: 'Attach to Incident',
        //   icon: 'insight-icon-addnew',
        // },
      ];
      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'Column_Name_Device_Name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'Name',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'totalUsageMB',
          label: 'total_data_usage_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'totalUsage',
        },
        {
          id: 'totalDownloadMB',
          label: 'data_usage_table_download',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'totalDownload',
        },
        {
          id: 'totalUploadMB',
          label: 'data_usage_table_upload',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'totalUpload',
        },
        {
          id: 'timeCellHour',
          label: 'timeCellHour',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'timeCell',
        },
        {
          id: 'usageCellMB',
          label: 'usageCellMB',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'usageCell',
        },
        {
          id: 'timeWiFiHour',
          label: 'timeWiFiHour',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'timeWiFi',
        },
        {
          id: 'usageWiFiMB',
          label: 'WifiUsage_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'usageWiFi',
        },
        {
          id: 'totalEthernetUsageMB',
          label: 'EthernetUsage_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'totalEthernetUsage',
        },
        {
          id: 'timeRoamHour',
          label: 'timeRoamHour',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'timeRoam',
        },
        {
          id: 'usageRoamMB',
          label: 'usageRoamMB',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'usageRoam',
        },
        {
          id: 'mostUsedApp',
          label: 'mostUsedApp',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'mostUsedApp',
        },
        {
          id: 'mostUsedBrowser',
          label: 'mostUsedBrowser',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'mostUsedBrowser',
        },
        {
          id: 'usageTetherMB',
          label: 'usageTetherMB',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'usageTether',
        },
        {
          id: 'timeVpnHour',
          label: 'timeVpnHour',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'timeVpn',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Manufacturer',
          label: 'label_Manufacturer',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  const pageIds = [
    findPageId(app.pages, DATA_USAGE_PAGE),
    // findPageId(devApp.pages, DEVICE_SUMMARY),
  ];

  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_DATA_USAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}

export function mapWebAppUsageConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.claims = [ProfilePermissionEnum.ProfileViewWebAppUsageDashboard];
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  app.icon = 'insight-icon-web-app';
  patchPage(app, WEBAPP_USAGE_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.group = 'device_filter_app_group';
    app.name = 'Device_Filter_Web_App_Usage';
    page.selectedTab = WEBAPP_USAGE_DEVICE_TABLE;
    page.pageCategory = PageCategory.DEVICE;
    page.canManageApps = true;
    page.predefinedCharts = [
      {
        category: {
          name: 'Device_Charts_Category_Name_Caption',
          icon: 'insight-icon-device',
          tooltip: 'App_Usage_Charts_Category_Header_Device_Tooltip',
        },
        charts: [
          buildChartOptions(
            page.charts,
            'webAppManufacturers',
            'Device_Charts_Category_Chart_Caption_Top_Manufacturers'
          ),
          buildChartOptions(page.charts, 'webAppModel', 'Device_Charts_Category_Chart_Caption_Top_Models'),
          buildChartOptions(page.charts, 'webAppOSVersion', 'Device_Charts_Category_Chart_Caption_Top_OS_Versions'),
        ],
      },
      {
        category: {
          name: 'App_Usage_Charts_Category_Header_Application',
          icon: '',
          tooltip: 'App_Usage_Charts_Category_Header_Application_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'webSystemRegular', 'webapp_Usage_Charts_Category_App_Categories'),
          buildChartOptions(page.charts, 'webTopVisitFrequency', 'webapp_Usage_Charts_Category_Top_Visited_App'),
          buildChartOptions(page.charts, 'webVisitFrequency', 'webapp_Usage_Charts_Category_Visited_App'),
          buildChartOptions(page.charts, 'webTopForegroundTime', 'webapp_Usage_Charts_Category_Top_Foreground_Usage'),
          buildChartOptions(page.charts, 'webForegroundTime', 'webapp_Usage_Charts_Category_Foreground_Usage'),
          buildChartOptions(page.charts, 'webTopErrorCodes', 'webapp_Usage_Charts_Category_Top_Error_Codes'),
          buildChartOptions(page.charts, 'webErrorCodes', 'webapp_chart_error_codes_name'),
          buildChartOptions(page.charts, 'webAppLoadTime', 'webapp_Usage_Charts_Category_Load_Time'),
          buildChartOptions(page.charts, 'webAppFirstByteTime', 'webapp_Usage_Charts_Category_First_Byte'),
        ],
      },
    ];

    patchChart(page.charts, 'webAppUsageDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.name = 'devices_summary_name';
      chart.layout = '33p';
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.items = WEB_APP_USAGE_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'webAppManufacturers', (chart: DonutChartConfig) => {
      chart.fullName = 'Manufacturers_fullName';
      chart.reportSummary = 'Manufacturers_reportsummary';
      chart.name = 'webapp_chart_manufacturers_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_manufacturer_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_manufacturer_tooltip_value", this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.tag = {
        name: 'webapp_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
    });

    patchChart(page.charts, 'webAppModel', (chart: DonutChartConfig) => {
      chart.fullName = 'app_chart_model_fullname';
      chart.reportSummary = 'app_chart_model_reportsummary';
      chart.name = 'webapp_chart_models_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_model_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_model_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_model_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_model_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.tag = {
        name: 'webapp_chart_models_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
    });

    patchChart(page.charts, 'webAppOSVersion', (chart: BarChartConfig) => {
      chart.fullName = 'app_chart_osVersion_fullname';
      chart.reportSummary = 'app_chart_osVersion_reportsummary';
      chart.name = 'webapp_chart_OS_versions_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'device_count_label',
      };
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("app_chart_osVersion_tooltip_label", this.point.name)}`',
        labelArgs: ['app_chart_osVersion_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("app_chart_osVersion_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'app_chart_osVersion_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'webapp_chart_OS_versions_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.colorByPoint = true;
      chart.integerOnly = true;
    });

    patchChart(page.charts, 'webSystemRegular', (chart: DonutChartConfig) => {
      chart.fullName = 'webapp_chart_systemRegular_fullname';
      chart.reportSummary = 'webapp_chart_systemRegular_reportsummary';
      chart.name = 'webapp_chart_app_categories_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_chart_systemRegular_tooltip_label", this.point.name)}`',
        labelArgs: ['webapp_chart_systemRegular_tooltip_label', 'this.point.name'],
        value:
          '`${xsI18nPipe.transform("webapp_chart_systemRegular_tooltip_value", this.point.y, (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,\'\'), this.point.devcount)}`',
        valueArgs: [
          'webapp_chart_systemRegular_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.tag = {
        name: 'webapp_chart_app_categories_name',
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
    });

    patchChart(page.charts, 'webTopVisitFrequency', (chart: BarChartConfig) => {
      chart.name = 'webapp_chart_visited_apps_name';
      chart.fullName = 'webapp_chart_visit_frequency_fullName';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'label_web_Applications',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'web_as_chart_visitTimeline_name',
      };
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_chart_visitFrequency_tooltip_label", this.point.label)}`',
        labelArgs: ['webapp_chart_visitFrequency_tooltip_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("webapp_chart_visitFrequency_tooltip_value", this.point.y, this.point.contribDevCount)}`',
        valueArgs: ['webapp_chart_visitFrequency_tooltip_value', 'this.point.y', 'this.point.contribDevCount'],
      };
      chart.tag = {
        name: 'webapp_chart_visited_apps_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.scrollableX = true;
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard(page.charts, 'webVisitFrequency', (chart: LineChartConfig) => {
      chart.fullName = 'webapp_chart_visited_apps_time_fullname';
      chart.name = 'webapp_chart_visited_apps_time_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = false;
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_usage_time_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'webapp_chart_visits_label',
      };
      chart.tag = { name: 'webapp_chart_visited_apps_time_name' };
      chart.rotateLabels = true;
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'webapp_chart_visitFrequencyTimeline_tooltip_value',
      };
      chart.legendDisabled = true;
    });

    patchChart(page.charts, 'webTopForegroundTime', (chart: BarChartConfig) => {
      chart.name = 'webapp_chart_foreground_name';
      chart.fullName = 'webapp_chart_foreground_fullName';
      chart.integerOnly = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_chart_foregroundTime_tooltip_label", this.point.label)}`',
        labelArgs: ['webapp_chart_foregroundTime_tooltip_label', 'this.point.label'],
        value:
          '`${xsI18nPipe.transform("webapp_chart_foregroundTime_tooltip_value", this.point.yUnit, this.point.totalTimeUnit, this.point.contribDevCount)}`',
        valueArgs: [
          'webapp_chart_foregroundTime_tooltip_value',
          'this.point.yUnit',
          'this.point.totalTimeUnit',
          'this.point.contribDevCount',
        ],
      };
      chart.tag = {
        name: 'webapp_chart_foreground_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'label_web_Applications',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_chart_foreground_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchCard(page.charts, 'webForegroundTime', (chart: LineChartConfig) => {
      chart.fullName = 'webapp_chart_foreground_time_fullname';
      chart.name = 'webapp_chart_foreground_time_name';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_usage_time_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.type = ChartTypeEnum.LINE;
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = false;
      chart.metric = {
        metricId: 'y',
        label: 'webapp_chart_foreground_label',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'webapp_chart_foreground_time_tooltip_value',
      };
      chart.rotateLabels = true;
      chart.legendDisabled = true;
    });

    patchChart(page.charts, 'webTopErrorCodes', (chart: StackedBarChartConfig) => {
      chart.name = 'webapp_chart_top_errors_name';
      chart.fullName = 'webapp_chart_top_errors_fullname';
      chart.integerOnly = true;
      chart.allowPointSelect = true;
      chart.tooltip = {
        label: 'webapp_chart_topN_error_tooltip_label',
        value: 'webapp_chart_topN_error_tooltip_value',
      };
      chart.tag = {
        name: 'webapp_chart_top_errors_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filter = {
        attribute: 'WebAppId',
        childAttribute: 'ErrorId',
        type: 'mapped',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.childDimension = {
        dimensionId: 'ErrorName',
        label: 'webapp_chart_api_error_childDimension_label',
      };
      chart.scrollableX = true;
      chart.type = ChartTypeEnum.STACKED_BAR;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'WebAppName',
        label: 'label_web_Applications',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'Metric',
        label: 'number_of_errors_label',
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
      chart.fixedColors = [
        {
          label: 'webapp_client_errors',
          color: HighchartsConstant.webAppError.ClientError,
        },
        {
          label: 'webapp_server_errors',
          color: HighchartsConstant.webAppError.ServerError,
        },
        {
          label: 'webapp_ssl_errors',
          color: HighchartsConstant.webAppError.SSLError,
        },
        {
          label: 'webapp_webView_errors',
          color: HighchartsConstant.webAppError.WebViewError,
        },
      ];
    });

    patchChart(page.charts, 'webErrorCodes', (chart: LineChartConfig) => {
      chart.fullName = 'webapp_chart_error_codes_fullname';
      chart.name = 'webapp_chart_error_codes_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.formatDate = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.dimension = {
        dimensionId: 'bucketLabel',
        label: 'webapp_chart_usage_time_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'number_of_errors_label',
      };
      chart.metrics = [
        {
          id: 'clientY',
          countId: 'clientDevCount',
          metricName: 'webapp_client_errors',
        },
        {
          id: 'serverY',
          countId: 'serverDevCount',
          metricName: 'webapp_server_errors',
        },
        {
          id: 'sslY',
          countId: 'sslDevCount',
          metricName: 'webapp_ssl_errors',
        },
        {
          id: 'webViewY',
          countId: 'webDevCount',
          metricName: 'webapp_webView_errors',
        },
      ];
      chart.tag = { name: 'webapp_chart_error_codes_name' };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'webapp_error_timeline_tooltip_value',
      };
      chart.rotateLabels = true;
      chart.legendDisabled = false;
      chart.legends = ['webapp_client_errors', 'webapp_server_errors', 'webapp_ssl_errors', 'webapp_webView_errors'];
    });

    patchChart(page.charts, 'webAppLoadTime', (chart: BarChartConfig) => {
      chart.name = 'webapp_chart_load_time_name';
      chart.fullName = 'webapp_chart_load_time_fullname';
      chart.integerOnly = true;
      chart.allowPointSelect = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_chart_app_time_tooltip_label", this.point.label)}`',
        labelArgs: ['webapp_chart_app_time_tooltip_label', 'this.point.label'],
        value: '`${xsI18nPipe.transform("webapp_chart_app_loadtime_tooltip_value", this.point.y)}`',
        valueArgs: ['webapp_chart_app_loadtime_tooltip_value', 'this.point.y'],
      };
      chart.tag = {
        name: 'webapp_chart_load_time_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'web_app_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_time_label',
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, 'webAppFirstByteTime', (chart: BarChartConfig) => {
      chart.name = 'webapp_chart_first_byte_name';
      chart.fullName = 'webapp_first_byte_fullname';
      chart.integerOnly = true;
      chart.allowPointSelect = true;
      chart.tooltip = {
        label: '`${xsI18nPipe.transform("webapp_chart_app_time_tooltip_label", this.point.label)}`',
        labelArgs: ['webapp_chart_app_time_tooltip_label', 'this.point.label'],
        value: '`${xsI18nPipe.transform("webapp_chart_app_firstbyte_tooltip_value", this.point.y)}`',
        valueArgs: ['webapp_chart_app_firstbyte_tooltip_value', 'this.point.y'],
      };
      chart.tag = {
        name: 'webapp_chart_first_byte_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'label',
        label: 'web_app_label',
        noDataLabel: 'no_data_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.metric = {
        metricId: 'metric',
        label: 'webapp_time_label',
      };
      chart.uiSettings = [CHART_UI_BAR_TOPN_MANY, CHART_UI_DONUT_LEAST_MOST];
    });

    patchChart(page.charts, WEBAPP_USAGE_DEVICE_TABLE, (chart: TableConfig) => {
      chart.name = 'switcher_devices_title';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
        // {
        //   action: 'attachIncident',
        //   tooltip: 'Attach to Incident',
        //   icon: 'insight-icon-addnew',
        // },
      ];
      chart.tableAttributes = [
        {
          id: 'McDeviceName',
          label: 'Column_Name_Device_Name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'Column_Name_Device_Id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'DeviceGroup',
          label: 'Column_Name_Device_Group',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'Model',
          label: 'Column_Name_Model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'OSVersion',
          label: 'Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'OSVersionAsPaddedString',
        },
        {
          id: 'MostVisitedApp',
          label: 'mostVisitedWebApp_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'visits',
          label: 'totalWebAppVisits_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'MostUsedApp',
          label: 'mostUsedWebApp_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },
        {
          id: 'clientErrors',
          label: 'client_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'serverErrors',
          label: 'server_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'webViewErrors',
          label: 'webview_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'sslErrors',
          label: 'ssl_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });

    patchChart(page.charts, WEBAPP_USAGE_APP_TABLE, (chart: TableConfig) => {
      chart.name = 'label_Web_Applications';
      chart.tableAttributes = [
        {
          id: 'webAppName',
          label: 'webapp_table_app_column_name_label',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'appClicked',
            eventParams: {
              surfApp: true,
            },
          },
        },
        {
          id: 'category',
          label: 'webapp_category_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'visits',
          label: 'webapp_visit_label',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'consumedTime',
          label: 'foreground_label',
          renderer: TableRenderEnum.STRING,
          primaryColumnSortId: 'consumedTimeInMillisecs',
        },

        {
          id: 'clientErrors',
          label: 'client_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'serverErrors',
          label: 'server_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'webViewErrors',
          label: 'webview_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'sslErrors',
          label: 'ssl_error_label',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  const pageIds = [findPageId(app.pages, WEBAPP_USAGE_PAGE)];

  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_WEBAPP_USAGE);

  return { ...app, pageIds, childPageIds, defaultTabId };
}

export function mapWifiConfig(
  app: ApplicationConfigWithPages,
  devApp: ApplicationConfigWithPages
): ApplicationConfigWithPages {
  app.claims = [ProfilePermissionEnum.ProfileViewWifiDashboard];
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.DEVICE;
  patchPage(app, WIFI_PAGE, page => {
    page.filterBarVisible = true;
    page.filterBarConfig = DEFAULT_DEVICE_FILTER_BAR_CONFIG;
    app.group = 'device_filter_network_group';
    app.name = 'wf_name';
    page.hourEnabled = true;
    page.hourSelectorType = HourSelectorType.CHECKBOX;
    page.multiHourSelector = true;
    page.pageCategory = PageCategory.DEVICE;
    page.predefinedCharts = [
      {
        category: {
          name: 'Device_Charts_Category_Name_Caption',
          icon: 'insight-icon-device',
          tooltip: 'App_Usage_Charts_Category_Header_Device_Tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'wfManufacturers', 'Device_Charts_Category_Chart_Caption_Top_Manufacturers'),
          buildChartOptions(page.charts, 'wfModel', 'Device_Charts_Category_Chart_Caption_Top_Models'),
          buildChartOptions(page.charts, 'wfOSVersion', 'Device_Charts_Category_Chart_Caption_Top_OS_Versions'),
        ],
      },
      {
        category: {
          name: 'wf_predefined_signal_name',
          icon: '',
          tooltip: 'wf_predefined_signal_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'wfSignalStrength', 'wf_signal_strength_name'),
          buildChartOptions(page.charts, 'wfFreqBand', 'wf_predefined_freq_band'),
          buildChartOptions(page.charts, 'wfSignalStrengthTS', 'wf_avg_signal_strength_name'),
        ],
      },
      {
        category: {
          name: 'wf_predefined_connection_name',
          icon: '',
          tooltip: 'wf_predefined_connection_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'wfMeteredTimeTS', 'wf_predefined_avg_metered_unmetered'),
          buildChartOptions(page.charts, 'wfMeteredDevicesTS', 'wf_predefined_metered_unmetered_connections'),
          buildChartOptions(page.charts, 'wfMeteredTimeTop', 'wf_predefined_metered_unmetered_connections_most'),
        ],
      },
      {
        category: {
          name: 'wf_predefined_usage_name',
          icon: '',
          tooltip: 'wf_predefined_usage_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'wfSSIDDevicesTS', 'wf_predefined_network_usage_devices'),
          buildChartOptions(page.charts, 'wfBSSIDDevicesTS', 'wf_predefined_access_point_usage_devices'),
          buildChartOptions(page.charts, 'wfSSIDDevicesTop', 'wf_predefined_network_ssid_most'),
          buildChartOptions(page.charts, 'wfBSSIDDevicesTop', 'wf_predefined_access_points_bssid_most'),
        ],
      },
      {
        category: {
          name: 'wf_predefined_disconnects_name',
          icon: '',
          tooltip: 'wf_predefined_disconnects_tooltip',
        },
        charts: [
          buildChartOptions(page.charts, 'wfBSSIDDisconnectsTop', 'wf_predefined_access_points_bssid_disconnects_most'),
          buildChartOptions(page.charts, 'wfDeviceDisconnectsTop', 'wf_predefined_device_disconnects_most'),
          buildChartOptions(page.charts, 'wfDisconnectTS', 'wf_predefined_number_of_device_disconnects'),
        ],
      },
    ];

    patchChart(page.charts, 'wfDevSummary', (chart: SummaryChartConfig) => {
      chart.elementType = ElementTypeEnum.SUMMARY;
      chart.fullName = 'devices_summary_full_name_dashboards';
      chart.name = 'devices_summary_name';
      chart.layout = '33p';
      chart.items = DEVICE_SUMMARY_CHART_ITEMS;
    });

    patchChart(page.charts, 'wfManufacturers', (chart: DonutChartConfig) => {
      chart.fullName = 'Manufacturers_fullName';
      chart.reportSummary = 'Manufacturers_reportsummary';
      chart.name = 'wf_chart_manufacturers_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'wf_chart_manufacturers_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_manufacturer_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_manufacturer_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_manufacturer_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_manufacturer_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfModel', (chart: DonutChartConfig) => {
      chart.fullName = 'Model_fullName';
      chart.reportSummary = 'Model_reportsummary';
      chart.name = 'wf_chart_model_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'wf_chart_model_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_model_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_model_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_models_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_models_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfOSVersion', (chart: BarChartConfig) => {
      chart.fullName = 'OSVersion_fullName';
      chart.reportSummary = 'OSVersion_reportsummary';
      chart.name = 'wf_chart_os_version_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'name',
        label: 'android_ver_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'device_count_label',
      };
      chart.tag = {
        name: 'wf_chart_os_version_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('data_chart_os_version_tooltip_label', this.point.name)}`",
        labelArgs: ['data_chart_os_version_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfSignalStrength', (chart: DonutChartConfig) => {
      chart.fullName = 'wf_signal_strength_fullname';
      chart.name = 'wf_signal_strength_name';
      chart.type = ChartTypeEnum.DONUT;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tag = {
        name: 'wf_signal_strength_name',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf-signal-strength-name', this.point.name)}`",
        labelArgs: ['wf-signal-strength-name', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.filter = {
        attribute: 'filter',
        type: 'mapped',
      };
      chart.uiSettings = [
        {
          id: 'WifiSignalStrength',
          controlType: 'THRESHOLD',
          type: 'number',
          displayType: 'dropdown',
          name: 'select_signal_strength_wifi',
          options: [],
        },
      ];
      chart.fixedColors = [
        {
          label: '{SignalStrength_4}',
          color: HighchartsConstant.signalStrengthBucket.nodata,
        },
        {
          label: '{SignalStrength_0}',
          color: HighchartsConstant.signalStrengthBucket.nosignal,
        },
        {
          label: '{SignalStrength_3}',
          color: HighchartsConstant.signalStrengthBucket.bad,
        },
        {
          label: '{SignalStrength_2}',
          color: HighchartsConstant.signalStrengthBucket.average,
        },
        {
          label: '{SignalStrength_1}',
          color: HighchartsConstant.signalStrengthBucket.good,
        },
      ];
    });
    patchChart(page.charts, 'wfFreqBand', (chart: BarChartConfig) => {
      chart.fullName = 'wf_freq_band_fullname';
      chart.name = 'wf_freq_band_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf-freq-band-name', xsI18nPipe.transform(this.point.name))}`",
        labelArgs: ['wf-freq-band-name', 'xsI18nPipe.transform(this.point.name)'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_freq_band_name',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'wf_freq_band_name',
      };
      chart.metric = {
        metricId: 'y',
        label: 'device_count_label',
      };
    });
    patchChart(page.charts, 'wfSignalStrengthTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_avg_signal_strength_fullname';
      chart.name = 'wf_avg_signal_strength_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_avg_signal_strength_dbm',
      };
      chart.tag = {
        name: 'wf_avg_signal_strength_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_avg_signal_strength_tooltip_value',
      };
      chart.legendDisabled = true;
      chart.formatDate = true;
      chart.allowNegativeValue = true;
      chart.dateFormatStr = 'LL';
      chart.hourSwitch = true;
    });
    patchChart(page.charts, 'wfMeteredTimeTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_metered_unmetered_avg_time_fullname';
      chart.name = 'wf_metered_unmetered_avg_time_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'wf_metered_unmetered_connections_avg_time',
      };
      chart.metrics = [
        {
          id: 'y1',
          countId: 'cntMeteredDev',
          timeFormatValueId: 'hourMinute1',
          metricName: 'wf_metered',
        },
        {
          id: 'y2',
          countId: 'cntUnmeteredDev',
          timeFormatValueId: 'hourMinute2',
          metricName: 'wf_unmetered',
        },
      ];
      chart.tag = {
        name: 'wf_metered_unmetered_avg_time_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_metered_unmetered_avg_time_tooltip_value',
      };
      chart.formatDate = true;
      chart.formatTime = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = false;
      chart.legends = ['wf_metered', 'wf_unmetered'];
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });
    patchChart(page.charts, 'wfMeteredDevicesTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_metered_unmetered_devices_fullname';
      chart.name = 'wf_metered_unmetered_devices_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: undefined,
        label: 'number_of_devices_label',
      };
      chart.metrics = [
        {
          id: 'cntMeteredDev',
          metricName: 'wf_metered',
        },
        {
          id: 'cntUnmeteredDev',
          metricName: 'wf_unmetered',
        },
      ];
      chart.tag = {
        name: 'wf_metered_unmetered_devices_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_metered_unmetered_devices_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = false;
      chart.legends = ['wf_metered', 'wf_unmetered'];
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });
    patchChart(page.charts, 'wfMeteredTimeTop', (chart: BarChartConfig) => {
      chart.fullName = 'wf_metered_unmetered_time_top_fullname';
      chart.name = 'wf_metered_unmetered_time_top_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_metered_unmetered_time_top_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'wf_metered_unmetered_time_top_name',
        parameters: ['mostLeast'],
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_metered_unmetered_time_top_metric_label',
      };
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf_average_time_metered', this.point.name)}`",
        labelArgs: ['wf_average_time_metered', 'this.point.name'],
        value: "`${xsI18nPipe.transform('wf_avg_time_metered_conn_tooltip_value', this.point.hourMinute)}`",
        valueArgs: ['wf_avg_time_metered_conn_tooltip_value', 'this.point.hourMinute'],
      };
      chart.integerOnly = true;
      chart.scrollableX = true;
      chart.inverted = true;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfSSIDDevicesTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_network_usage_devices_ssid_fullname';
      chart.name = 'wf_network_usage_devices_ssid_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'CollectedDate',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: null,
        label: 'wf_network_usage_devices_ssid_metric_label',
      };
      chart.tag = {
        name: 'wf_network_usage_devices_ssid_name',
        parameters: ['mostLeast'],
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_network_usage_devices_ssid_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.dynamicLegend = true;
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });
    patchChart(page.charts, 'wfBSSIDDevicesTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_access_point_usage_bssid_fullname';
      chart.name = 'wf_access_point_usage_bssid_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'CollectedDate',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: null,
        label: 'wf_access_point_usage_bssid_metric_label',
      };
      chart.tag = {
        name: 'wf_access_point_usage_bssid_name',
        parameters: ['mostLeast'],
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_access_point_usage_bssid_tooltip_value',
      };
      chart.formatDate = true;
      chart.dateFormatStr = 'LL';
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
      chart.dynamicLegend = true;
      chart.hourSwitch = true;
      chart.integerOnly = true;
    });
    patchChart(page.charts, 'wfSSIDDevicesTop', (chart: BarChartConfig) => {
      chart.fullName = 'wf_network_ssid_fullname';
      chart.name = 'wf_network_ssid_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.scrollableX = true;
      chart.layout = '33p';
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf_network_ssid_tooltip_label', this.point.name)}`",
        labelArgs: ['wf_network_ssid_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_network_ssid_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'wf_network_ssid_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'y',
        label: 'device_count_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfBSSIDDevicesTop', (chart: BarChartConfig) => {
      chart.fullName = 'wf_access_points_bssid_fullname';
      chart.name = 'wf_access_points_bssid_name';
      chart.type = ChartTypeEnum.COLUMN;
      chart.elementType = 'chart';
      chart.scrollableX = true;
      chart.layout = '33p';
      chart.integerOnly = true;
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf_access_points_bssid_tooltip_label', this.point.name)}`",
        labelArgs: ['wf_access_points_bssid_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.y',
          'percentage(this.point.y, this.point.devcount)',
          'this.point.devcount',
        ],
      };
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_access_points_bssid_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'wf_access_points_bssid_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'y',
        label: 'device_count_label',
      };
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfBSSIDDisconnectsTop', (chart: BarChartConfig) => {
      chart.fullName = 'wf_access_points_bssid_disconnects_fullname';
      chart.name = 'wf_access_points_bssid_disconnects_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.elementType = 'chart';
      chart.layout = '33p';
      chart.tooltip = {
        label: "`${xsI18nPipe.transform('wf_access_points_bssid_disconnects_tooltip_label', this.point.name)}`",
        labelArgs: ['wf_access_points_bssid_disconnects_tooltip_label', 'this.point.name'],
        value:
          "`${xsI18nPipe.transform('data_chart_os_version_tooltip_value', this.point.devcount, (this.point.devcount/this.point.enrolledcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.enrolledcount)}`",
        valueArgs: [
          'data_chart_os_version_tooltip_value',
          'this.point.devcount',
          'percentage(this.point.devcount, this.point.enrolledcount)',
          'this.point.enrolledcount',
        ],
      };
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_access_points_bssid_disconnects_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.tag = {
        name: 'wf_access_points_bssid_disconnects_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_device_disconnects_most_metric_label',
      };
      chart.scrollableX = true;
      chart.inverted = true;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfDeviceDisconnectsTop', (chart: BarChartConfig) => {
      chart.fullName = 'wf_device_disconnects_most_fullname';
      chart.name = 'wf_device_disconnects_most_name';
      chart.type = ChartTypeEnum.CYLINDER;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.tooltip = {
        label:
          "`${xsI18nPipe.transform('wf_device_disconnects_most_tooltip_label', truncatePipe.transform(this.point.name, [80]))}`",
        labelArgs: ['wf_device_disconnects_most_tooltip_label', 'truncatePipe.transform(this.point.name, [80])'],
        value: "`${xsI18nPipe.transform('wf_device_disconnects_most_name_tooltip_value', this.point.y)}`",
        valueArgs: ['wf_device_disconnects_most_name_tooltip_value', 'this.point.y'],
      };
      chart.integerOnly = true;
      chart.dimension = {
        dimensionId: 'name',
        label: 'wf_device_disconnects_most_dimension_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_device_disconnects_most_metric_label',
      };
      chart.tag = {
        name: 'wf_device_disconnects_most_name',
        parameters: ['topN', 'mostLeast'],
      };
      chart.scrollableX = true;
      chart.inverted = true;
      chart.filters = {
        topN: 5,
        mostLeast: MostLeastEnum.MOST,
      };
      chart.uiSettings = [WF_CHART_UI_BAR_TOPN, CHART_UI_DONUT_LEAST_MOST];
    });
    patchChart(page.charts, 'wfDisconnectTS', (chart: LineChartConfig) => {
      chart.fullName = 'wf_number_of_device_disconnects_fullname';
      chart.name = 'wf_number_of_device_disconnects_name';
      chart.type = ChartTypeEnum.LINE;
      chart.elementType = 'chart';
      chart.layout = '50p';
      chart.dimension = {
        dimensionId: 'x',
        label: 'xs_time_interval_label',
        noDataLabel: 'no_data_label',
      };
      chart.metric = {
        metricId: 'y',
        label: 'wf_number_of_device_disconnects_metric_label',
      };
      chart.tag = {
        name: 'wf_number_of_device_disconnects_name',
      };
      chart.tooltip = {
        label: 'xs_reported_date_label',
        value: 'wf_number_of_device_disconnects_tooltip_label',
      };
      chart.formatDate = true;
      chart.integerOnly = true;
      chart.dateFormatStr = 'LL';
      chart.legendDisabled = true;
      chart.hourSwitch = true;
    });
    patchChart(page.charts, WIFI_TABLE, (chart: TableConfig) => {
      chart.name = 'Ticket_List_Devices_Header';
      chart.selectionType = InsSelectionType.multiple;
      chart.selectionText = 'device_selected_label';
      chart.selectionTextPlural = 'device_selected_label';
      chart.rowActions = [
        {
          action: 'customAttributes',
          tooltip: 'custom_attr_title',
          icon: 'insight-icon-deviceinfo',
        },
      ];
      chart.tableAttributes = [
        {
          id: 'DeviceName',
          label: 'wf_table_device_name',
          renderer: TableRenderEnum.LINK,
          rendererParams: {
            eventId: 'deviceClicked',
            icons: [
              {
                iconClass: 'insight-icon-type-DeviceAndroidGeneric',
              },
            ],
          },
        },
        {
          id: 'McDeviceId',
          label: 'wf_table_device_id',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'GroupPath',
          label: 'wf_table_device_group',
          renderer: TableRenderEnum.PATH,
          iconClass: '',
        },
        {
          id: 'FreqBand',
          label: 'wf_table_freq_band_id',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'totalUsage',
        },
        {
          id: 'SignalStrength',
          label: 'wf_table_signal_strength',
          renderer: TableRenderEnum.STRING,
        },
        {
          id: 'DisconnectCount',
          label: 'wf_table_disconnect_count',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'ManufacturerName',
          label: 'wf_table_manufacturer',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'SSID',
          label: 'wf_table_ssid',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'BSSID',
          label: 'wf_table_bssid',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
        {
          id: 'ModelName',
          label: 'wf_table_model',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
          primaryColumnSortId: 'DisplayAppName',
        },
        {
          id: 'OSVersion',
          label: 'Icu_Tab_Column_Name_Os_Version',
          renderer: TableRenderEnum.STRING,
          iconClass: '',
        },
      ];
    });
  });

  const pageIds = [
    findPageId(app.pages, WIFI_PAGE),
    // findPageId(devApp.pages, DEVICE_SUMMARY),
  ];

  const childPageIds = [
    findPageId(devApp.pages, DEVICE_BATTERY_USAGE),
    findPageId(devApp.pages, DEVICE_APP_USAGE),
    findPageId(devApp.pages, DEVICE_WEBAPP_USAGE),
    findPageId(devApp.pages, DEVICE_DATA_USAGE),
    findPageId(devApp.pages, DEVICE_LAST_KNOWN),
    findPageId(devApp.pages, DEVICE_SIGNAL_STRENGTH),
    findPageId(devApp.pages, SMART_BATTERY_SPOTLIGHT),
    findPageId(devApp.pages, DEVICE_WIFI),
  ];

  const defaultTabId = findPageId(devApp.pages, DEVICE_WIFI);

  return { ...app, pageIds, childPageIds, defaultTabId };
}
